import apiPublic, { authApiPublic } from '@/legacy/services/apiPublic';
import api, { authApi } from '@/legacy/services/api';
import type { LoginResponseDTO, RefreshResponseDTO } from '../';
import urls from '@/legacy/config/env';
import { LoginParams } from '../../domain';
import { parseCredentialResponse, type CredentialResponseDTO } from './parseCredentialResponse';

export const loginService = async ({
	username,
	password,
}: LoginParams): Promise<LoginResponseDTO> => {
	const response = await apiPublic.post(
		urls.auth.login,
		{ user: username, password: password },
		{
			headers: {
				'x-platform': 'web',
			},
		},
	);

	return response.data;
};

// authApiPublic (not `api`) — interceptor-free, so a refresh call can never trigger
// another refresh via the response interceptor.
export const refreshTokenService = async (
	refreshToken: string | null,
): Promise<RefreshResponseDTO> => {
	const response = await authApiPublic.post(
		urls.auth.refresh,
		{ refresh_token_jwt: refreshToken },
		{
			headers: {
				'x-platform': 'web',
			},
			// A rejected refresh token means the session expired — the interceptor logs the
			// user out; it is not a bug to report.
			silentStatuses: [400, 401],
		},
	);

	return response.data;
};

export const loginBetaUserService = async (token: string) => {
	const response = await apiPublic.post(urls.auth.loginBeta(token), {
		headers: { 'x-platform': 'web' },
	});
	return response.data;
};

export const loginBetaUserRegenerateCode = async (token: string) => {
	const response = await apiPublic.post(urls.auth.loginBetaRegenerateCode(token), {
		headers: { 'x-platform': 'web' },
	});
	return response.data;
};

export const logoutService = async () => {
	return await authApi.post(urls.auth.logout);
};

export const SWITCH_CREDENTIALS_TYPE = {
	COMPANY: 'company',
	DELEGATION: 'delegation',
} as const;

export type SwitchCredentialsType =
	(typeof SWITCH_CREDENTIALS_TYPE)[keyof typeof SWITCH_CREDENTIALS_TYPE];

export type SwitchCredentialsParams = {
	company: number;
	// Optional on purpose: the backend reads an ABSENT `delegation` as "the whole
	// company, all delegations". An empty array is a different contract, so the
	// company-wide scope omits the field rather than sending `[]`.
	delegation?: number[];
	type: SwitchCredentialsType;
};

// `api`, not `authApi`: the switch must carry the caller's CURRENT credential
// (the request interceptor attaches it) for the backend to re-scope off it, and
// it lives on the dashboard host like the `/login/delegations` call it replaces
// and its `/login/impersonate/:id` sibling — despite the `urls.auth` namespace,
// which groups paths rather than hosts.
export const switchCredentials = async (
	params: SwitchCredentialsParams,
): Promise<CredentialResponseDTO> => {
	const response = await api.post(urls.auth.loginSwitchCredentials, params);
	return parseCredentialResponse(response.data);
};
